import React, { useState } from "react";
import {
  FiBookOpen,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiCalendar,
  FiTarget,
  FiArrowRight,
} from "react-icons/fi";

// Mock assignment data
const mockAssignmentData = [
  {
    id: 1,
    title: "Calculus Problem Set",
    subject: "Mathematics",
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    status: "pending",
    marks: 50,
  },
  {
    id: 2,
    title: "Lab Report - Quantum Mechanics",
    subject: "Physics",
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    status: "pending",
    marks: 75,
  },
  {
    id: 3,
    title: "Essay on Climate Change",
    subject: "Environmental Science",
    dueDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    status: "overdue",
    marks: 100,
  },
  {
    id: 4,
    title: "Algorithm Implementation",
    subject: "Computer Science",
    dueDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    status: "submitted",
    marks: 60,
  },
  {
    id: 5,
    title: "Chemical Reactions Worksheet",
    subject: "Chemistry",
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    status: "pending",
    marks: 30,
  },
  {
    id: 6,
    title: "Literature Analysis",
    subject: "English",
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    status: "pending",
    marks: 80,
  },
];

const AssignmentTile = ({ assignment }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case "pending":
        return {
          icon: FiClock,
          color: "text-orange-600",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200",
          label: "Pending",
        };
      case "overdue":
        return {
          icon: FiAlertCircle,
          color: "text-red-600",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          label: "Overdue",
        };
      case "submitted":
        return {
          icon: FiCheckCircle,
          color: "text-green-600",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          label: "Submitted",
        };
      default:
        return {
          icon: FiClock,
          color: "text-gray-600",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          label: "Unknown",
        };
    }
  };

  const formatDueDate = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        text: `${Math.abs(diffDays)}d overdue`,
        isOverdue: true,
      };
    } else if (diffDays === 0) {
      return {
        text: "Today",
        isOverdue: false,
      };
    } else if (diffDays === 1) {
      return {
        text: "Tomorrow",
        isOverdue: false,
      };
    } else if (diffDays <= 7) {
      return {
        text: `${diffDays}d left`,
        isOverdue: false,
      };
    } else {
      return {
        text: due.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        isOverdue: false,
      };
    }
  };

  const statusConfig = getStatusConfig(assignment.status);
  const StatusIcon = statusConfig.icon;
  const dueDateInfo = formatDueDate(assignment.dueDate);

  return (
    <div className={`assignment-tile ${assignment.status}`}>
      <div className="tile-left">
        <div className="assignment-main-info">
          <h4 className="assignment-title">{assignment.title}</h4>
          <div className="assignment-subject">
            <FiBookOpen size={12} />
            <span>{assignment.subject}</span>
          </div>
        </div>
      </div>

      <div className="tile-right">
        <div className="assignment-meta">
          <div className="meta-item due-date">
            <FiCalendar size={12} />
            <span
              className={
                dueDateInfo.isOverdue ? "text-red-600" : "text-gray-600"
              }
            >
              {dueDateInfo.text}
            </span>
          </div>

          <div className="meta-item marks">
            <FiTarget size={12} />
            <span>{assignment.marks}pts</span>
          </div>

          <div
            className={`status-badge ${statusConfig.bgColor} ${statusConfig.color} ${statusConfig.borderColor}`}
          >
            <StatusIcon size={11} />
            <span>{statusConfig.label}</span>
          </div>
        </div>

        {assignment.status !== "submitted" && (
          <button className="tile-action-btn">
            <FiArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

const Assignments = () => {
  const [assignments] = useState(mockAssignmentData);

  const pendingAssignments = assignments.filter(
    (assignment) =>
      assignment.status === "pending" || assignment.status === "overdue"
  );

  const overdueCount = assignments.filter(
    (assignment) => assignment.status === "overdue"
  ).length;

  return (
    <div className="assignments-container">
      <div className="assignments-header">
        <div className="header-content">
          <h3 className="assignments-title">Assignments</h3>
          <div className="assignments-stats">
            <span className="stat-bubble pending">
              {pendingAssignments.length}
            </span>
            {overdueCount > 0 && (
              <span className="stat-bubble overdue">{overdueCount}</span>
            )}
          </div>
        </div>
      </div>

      <div className="assignments-list">
        {pendingAssignments.length > 0 ? (
          pendingAssignments.map((assignment) => (
            <AssignmentTile key={assignment.id} assignment={assignment} />
          ))
        ) : (
          <div className="empty-state">
            <FiCheckCircle size={24} className="empty-icon" />
            <p className="empty-text">All assignments completed!</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .assignments-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          width: 100%;
          max-width: 400px;
        }

        .assignments-header {
          padding: 1.25rem 1.5rem 1rem;
          border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .assignments-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .assignments-stats {
          display: flex;
          gap: 0.5rem;
        }

        .stat-bubble {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          color: white;
        }

        .stat-bubble.pending {
          background: #f59e0b;
        }

        .stat-bubble.overdue {
          background: #ef4444;
        }

        .assignments-list {
          max-height: 350px;
          overflow-y: auto;
          padding: 0.25rem;
        }

        .assignments-list::-webkit-scrollbar {
          width: 4px;
        }

        .assignments-list::-webkit-scrollbar-track {
          background: #f8fafc;
        }

        .assignments-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }

        .assignment-tile {
          display: flex;
          flex-direction: column;
          padding: 0.875rem;
          margin: 0.25rem 0.5rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          transition: all 0.2s ease;
          cursor: pointer;
          gap: 0.75rem;
        }

        .assignment-tile:hover {
          border-color: #cbd5e1;
          background: #fafbfb;
          transform: translateY(-1px);
        }

        .assignment-tile.overdue {
          border-left: 3px solid #ef4444;
        }

        .assignment-tile.submitted {
          opacity: 0.7;
          cursor: default;
        }

        .assignment-tile.submitted:hover {
          transform: none;
          background: white;
          border-color: #e2e8f0;
        }

        .tile-left {
          width: 100%;
        }

        .assignment-main-info {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
          margin-bottom: 0.5rem;
        }

        .assignment-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .assignment-subject {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.7rem;
          color: #64748b;
        }

        .tile-right {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }

        .assignment-meta {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.7rem;
          color: #64748b;
          white-space: nowrap;
        }

        .meta-item.due-date {
          min-width: 60px;
        }

        .meta-item.marks {
          min-width: 40px;
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.2rem 0.4rem;
          border-radius: 6px;
          font-size: 0.65rem;
          font-weight: 500;
          border: 1px solid;
          white-space: nowrap;
        }

        .tile-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .tile-action-btn:hover {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2rem;
          color: #64748b;
        }

        .empty-icon {
          margin-bottom: 0.75rem;
          color: #10b981;
        }

        .empty-text {
          font-size: 0.875rem;
          margin: 0;
          color: #374151;
        }

        /* Color utility classes */
        .bg-red-50 {
          background-color: #fef2f2;
        }
        .bg-orange-50 {
          background-color: #fff7ed;
        }
        .bg-green-50 {
          background-color: #f0fdf4;
        }
        .bg-gray-50 {
          background-color: #f9fafb;
        }

        .text-red-600 {
          color: #dc2626;
        }
        .text-orange-600 {
          color: #ea580c;
        }
        .text-green-600 {
          color: #16a34a;
        }
        .text-gray-600 {
          color: #4b5563;
        }

        .border-red-200 {
          border-color: #fecaca;
        }
        .border-orange-200 {
          border-color: #fed7aa;
        }
        .border-green-200 {
          border-color: #bbf7d0;
        }
        .border-gray-200 {
          border-color: #e5e7eb;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .assignments-header {
            padding: 1rem;
          }

          .assignments-container {
            max-width: 100%;
          }

          .assignment-tile {
            padding: 0.75rem;
            margin: 0.25rem 0.25rem;
          }

          .assignment-meta {
            gap: 0.5rem;
          }

          .meta-item {
            font-size: 0.65rem;
          }

          .assignment-title {
            font-size: 0.8rem;
          }

          .assignment-subject {
            font-size: 0.65rem;
          }
        }

        @media (max-width: 480px) {
          .assignments-title {
            font-size: 1rem;
          }

          .assignment-meta {
            gap: 0.375rem;
          }

          .status-badge {
            padding: 0.15rem 0.35rem;
            font-size: 0.6rem;
          }

          .stat-bubble {
            min-width: 20px;
            height: 20px;
            font-size: 0.7rem;
          }

          .tile-action-btn {
            width: 22px;
            height: 22px;
          }
        }
      `}</style>
    </div>
  );
};

export default Assignments;
